/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
/**
 * Enhanced utility functions for handling paste operations in the rich text editor.
 * Provides sanitization, normalization, and conversion of pasted content from various sources.
 * Now preserves more formatting including margins, padding, borders, and spacing.
 * Also handles plain text paste with intelligent formatting reconstruction.
 */

import { formatPlainText, isPlainTextPaste } from './PlainTextFormatter';

/**
 * Sanitizes HTML content by removing scripts, styles, and cleaning up excessive whitespace.
 * Now preserves more inline styles for better formatting retention.
 */
export function sanitizeHTML(raw: string): string {
    const div = document.createElement('div');
    div.innerHTML = raw;

    // Remove scripts and style tags (but keep inline styles)
    div.querySelectorAll('script, style').forEach((n) => n.remove());

    // Clean up excessive &nbsp; characters from PDFs, but not all spaces
    div.innerHTML = div.innerHTML.replace(/(&nbsp;){3,}/g, '  ');

    // Remove empty wrapper elements that add no value
    // But preserve structural elements like lists, tables, etc.
    div.querySelectorAll('*').forEach((el) => {
        const tagName = el.tagName.toLowerCase();
        // Don't remove structural elements even if empty
        const structuralElements = [
            'ul',
            'ol',
            'li',
            'table',
            'tr',
            'td',
            'th',
            'thead',
            'tbody',
            'br',
            'hr',
        ];

        if (structuralElements.includes(tagName)) {
            return; // Skip structural elements
        }

        // Don't remove elements with style attributes - they might have important formatting
        const hasStyle = el.hasAttribute('style');

        // Remove empty spans/divs without attributes and no content
        if (
            !el.textContent?.trim() &&
            !hasStyle &&
            el.attributes.length === 0 &&
            el.children.length === 0
        ) {
            el.remove();
        }
    });

    return div.innerHTML;
}

/**
 * Converts inline styles to semantic HTML elements.
 * e.g., font-weight: 700 -> <strong>, font-style: italic -> <em>
 * Now preserves the original inline styles while also adding semantic tags.
 */
export function normalizeInlineStyles(root: HTMLElement): void {
    root.querySelectorAll('*').forEach((el) => {
        const htmlEl = el as HTMLElement;
        const style = htmlEl.style;
        if (!style) return;

        // Skip if already has semantic tags to avoid double-wrapping
        if (
            el.tagName.toLowerCase() === 'strong' ||
            el.tagName.toLowerCase() === 'b' ||
            el.tagName.toLowerCase() === 'em' ||
            el.tagName.toLowerCase() === 'i' ||
            el.tagName.toLowerCase() === 'u'
        ) {
            return;
        }

        const fw = style.fontWeight;
        const fs = style.fontStyle;
        const td = style.textDecoration;

        let needsWrapper = false;
        const wrappers: string[] = [];

        // Check for bold
        if (fw && (fw === 'bold' || fw === '700' || parseInt(fw, 10) >= 600)) {
            wrappers.push('strong');
            needsWrapper = true;
            // Keep the style for other potential uses but mark as converted
        }

        // Check for italic
        if (fs && fs.toLowerCase() === 'italic') {
            wrappers.push('em');
            needsWrapper = true;
        }

        // Check for underline
        if (td && td.includes('underline')) {
            wrappers.push('u');
            needsWrapper = true;
        }

        // Apply wrappers if needed
        if (needsWrapper && el.childNodes.length) {
            let content = el.innerHTML;

            // Wrap with semantic tags from inside out
            wrappers.reverse().forEach((tag) => {
                content = `<${tag}>${content}</${tag}>`;
            });

            el.innerHTML = content;

            // Clean up the inline styles that we converted
            if (wrappers.includes('strong')) style.fontWeight = '';
            if (wrappers.includes('em')) style.fontStyle = '';
            if (wrappers.includes('u')) {
                // Only remove underline, keep other text-decoration values
                const decorations = td.split(' ').filter((d) => d !== 'underline');
                style.textDecoration = decorations.join(' ');
            }
        }
    });
}

/**
 * Normalizes block elements by converting font sizes to heading levels and paragraphs.
 * Preserves inline styles for margins, padding, and alignment.
 */
export function normalizeBlocks(root: HTMLElement): void {
    // Map font-size to heading levels (heuristic)
    root.querySelectorAll('p, div').forEach((el) => {
        // Skip if this element is inside a list - don't normalize list items
        if (el.closest('ul, ol, li')) {
            return;
        }

        // Skip if this is a table cell
        if (el.closest('td, th')) {
            return;
        }

        const htmlEl = el as HTMLElement;
        const style = htmlEl.style;
        const fontSize = style?.fontSize ? parseFloat(style.fontSize) : undefined;

        // Check if element has significant content
        const hasContent = (el.textContent?.trim().length || 0) > 0;

        if (!hasContent) {
            // Preserve empty divs/paragraphs if they have spacing styles (they might be intentional spacing)
            const hasSpacingStyles =
                style &&
                (style.marginTop ||
                    style.marginBottom ||
                    style.paddingTop ||
                    style.paddingBottom ||
                    style.height ||
                    style.minHeight);

            if (!hasSpacingStyles) {
                return; // Skip empty elements without spacing
            }
        }

        if (fontSize && !isNaN(fontSize)) {
            let level: number | null = null;
            if (fontSize >= 24) level = 1;
            else if (fontSize >= 20) level = 2;
            else if (fontSize >= 18) level = 3;
            else if (fontSize >= 16) level = 4;
            else if (fontSize >= 14) level = 5;

            if (level) {
                const h = document.createElement(`h${level}`);
                // Preserve all attributes including style
                Array.from(el.attributes).forEach((attr) => {
                    h.setAttribute(attr.name, attr.value);
                });
                h.innerHTML = el.innerHTML;
                el.replaceWith(h);
                return;
            }
        }

        // Normalize div to paragraph, but preserve all styles
        if (
            el.tagName.toLowerCase() === 'div' &&
            hasContent &&
            !el.querySelector('ul, ol, li, table')
        ) {
            const p = document.createElement('p');
            // Preserve all attributes including style
            Array.from(el.attributes).forEach((attr) => {
                p.setAttribute(attr.name, attr.value);
            });
            p.innerHTML = el.innerHTML;
            el.replaceWith(p);
        }
    });
}

/**
 * Detects and converts bullet/numbered list patterns into proper HTML lists.
 * Only processes plain text paragraphs - preserves existing list structures.
 */
export function detectAndConvertLists(root: HTMLElement): void {
    // First, preserve existing lists by marking them
    const existingLists = root.querySelectorAll('ul, ol');
    existingLists.forEach((list) => {
        list.setAttribute('data-existing-list', 'true');
    });

    // Only process paragraphs that are NOT inside existing lists
    const paragraphs = Array.from(root.querySelectorAll('p')).filter(
        (p) => !p.closest('[data-existing-list="true"]') && !p.closest('ul') && !p.closest('ol')
    );

    // Group consecutive list-like paragraphs
    let currentList: HTMLUListElement | HTMLOListElement | null = null;
    let lastListType: 'ul' | 'ol' | null = null;

    paragraphs.forEach((p) => {
        const text = p.textContent?.trim() || '';

        // Check for bullet patterns (including various unicode bullets)
        const bulletLike = /^[•\-–—●○■□▪▫◆◇★☆►▸]/.test(text);
        // Check for numbered patterns - including (a), 1., 1), i., etc.
        const orderedLike = /^(\d+[.)]|[a-zA-Z][.)]|\([a-zA-Z0-9]+\)|[ivx]+[.)])\s+/.test(text);

        if (bulletLike || orderedLike) {
            const listType = orderedLike ? 'ol' : 'ul';

            // Start a new list if needed or if list type changed
            if (!currentList || lastListType !== listType) {
                currentList =
                    listType === 'ol' ? document.createElement('ol') : document.createElement('ul');
                lastListType = listType;

                // Preserve any margin/padding from the original paragraph on the list
                const pStyle = (p as HTMLElement).style;
                if (pStyle && (pStyle.marginLeft || pStyle.paddingLeft)) {
                    const listStyle = currentList.style;
                    if (pStyle.marginLeft) listStyle.marginLeft = pStyle.marginLeft;
                    if (pStyle.paddingLeft) listStyle.paddingLeft = pStyle.paddingLeft;
                }

                p.parentElement?.insertBefore(currentList, p);
            }

            // Create list item
            const li = document.createElement('li');

            // Remove list markers from the innerHTML
            li.innerHTML = p.innerHTML.replace(/^[•\-–—●○■□▪▫◆◇★☆►▸\d\w().ivx]+\s*/, '');

            // Preserve any inline styles from the paragraph (except margins that are now on the list)
            const pStyle = (p as HTMLElement).style;
            if (pStyle && pStyle.cssText) {
                const liStyle = li.style;
                // Copy styles but skip margin-left and padding-left (those are on the list now)
                if (pStyle.textIndent) liStyle.textIndent = pStyle.textIndent;
                if (pStyle.lineHeight) liStyle.lineHeight = pStyle.lineHeight;
                if (pStyle.marginTop) liStyle.marginTop = pStyle.marginTop;
                if (pStyle.marginBottom) liStyle.marginBottom = pStyle.marginBottom;
            }

            currentList.appendChild(li);
            p.remove();
        } else {
            // Reset list when we hit non-list content
            currentList = null;
            lastListType = null;
        }
    });

    // Clean up data attributes
    existingLists.forEach((list) => {
        list.removeAttribute('data-existing-list');
    });
}

/**
 * Removes Google Docs specific artifacts like classes and IDs.
 * Preserves useful inline styles.
 */
export function cleanGoogleDocsArtifacts(root: HTMLElement): void {
    root.querySelectorAll('*').forEach((el) => {
        // Remove Google Docs IDs
        if (el.id && el.id.match(/^(docs-|h\.|ftnt)/)) {
            el.removeAttribute('id');
        }

        // Remove Google Docs classes but keep our own classes
        const classes = el.getAttribute('class');
        if (classes) {
            // Remove Google Docs specific class patterns
            const cleanedClasses = classes
                .split(' ')
                .filter((c) => !c.match(/^(c\d+|docs-|lst-)/))
                .join(' ');

            if (cleanedClasses.trim()) {
                el.setAttribute('class', cleanedClasses.trim());
            } else {
                el.removeAttribute('class');
            }
        }
    });
}

/**
 * Handles page break conversion from pasted content.
 */
export function handlePageBreaks(root: HTMLElement): void {
    const pageBreaks = root.querySelectorAll('[style*="page-break"]');
    pageBreaks.forEach((el) => {
        const pageBreakDiv = document.createElement('div');
        pageBreakDiv.setAttribute('data-type', 'page-break');
        pageBreakDiv.setAttribute('class', 'page-break');
        el.replaceWith(pageBreakDiv);
    });
}

/**
 * Preserves table structure and styling from pasted content.
 * Ensures borders, padding, and cell alignment are maintained.
 */
export function preserveTableFormatting(root: HTMLElement): void {
    root.querySelectorAll('table').forEach((table) => {
        const htmlTable = table as HTMLElement;

        // Ensure table has border-collapse if it has borders
        if (htmlTable.style.border || table.querySelector('[style*="border"]')) {
            if (!htmlTable.style.borderCollapse) {
                htmlTable.style.borderCollapse = 'collapse';
            }
        }

        // Process all cells to ensure consistent border handling
        table.querySelectorAll('td, th').forEach((cell) => {
            const htmlCell = cell as HTMLElement;
            const style = htmlCell.style;

            // If cell has no explicit border but table does, apply table border to cells
            if (!style.border && htmlTable.style.border) {
                style.border = htmlTable.style.border;
            }

            // Ensure minimum padding for readability
            if (
                !style.padding &&
                !style.paddingLeft &&
                !style.paddingRight &&
                !style.paddingTop &&
                !style.paddingBottom
            ) {
                style.padding = '4px 8px';
            }
        });
    });
}

/**
 * Preserves spacing between elements (margins, padding).
 * Particularly important for PDF and Word document paste.
 */
export function preserveSpacing(root: HTMLElement): void {
    // Process all block-level elements
    root.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li, table, tr').forEach((el) => {
        const htmlEl = el as HTMLElement;
        const style = htmlEl.style;

        if (!style) return;

        // Convert various margin/padding formats to consistent values
        // e.g., "0pt" to "0px", "1em" stays as "1em"
        [
            'marginTop',
            'marginBottom',
            'marginLeft',
            'marginRight',
            'paddingTop',
            'paddingBottom',
            'paddingLeft',
            'paddingRight',
        ].forEach((prop) => {
            const value = style[prop as any];
            if (value) {
                // Convert pt to px (rough conversion: 1pt ≈ 1.33px)
                if (value.endsWith('pt')) {
                    const numValue = parseFloat(value);
                    style[prop as any] = `${Math.round(numValue * 1.33)}px`;
                }
            }
        });
    });
}

/**
 * Main paste processing function that handles both HTML and plain text pastes.
 * Detects paste type and applies appropriate transformations.
 */
export function processPastedContent(html: string): string {
    console.log('[Paste] Processing content, length:', html.length);

    // Check if this is a plain text paste (common with PDFs)
    if (isPlainTextPaste(html)) {
        console.log('[Paste] Detected plain text paste - applying intelligent formatting');

        // Extract plain text (strip any minimal HTML)
        const div = document.createElement('div');
        div.innerHTML = html;
        const plainText = div.textContent || div.innerText || '';

        // Format plain text into HTML with intelligent structure detection
        html = formatPlainText(plainText);
        console.log('[Paste] Plain text formatted, new length:', html.length);
    }

    // Now process as normal HTML
    const container = document.createElement('div');
    container.innerHTML = sanitizeHTML(html);

    // Apply transformation pipeline
    cleanGoogleDocsArtifacts(container);
    preserveSpacing(container);
    normalizeInlineStyles(container);
    normalizeBlocks(container);
    detectAndConvertLists(container);
    preserveTableFormatting(container);
    handlePageBreaks(container);

    return container.innerHTML;
}
